import { useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useQuery } from 'src/core/api/hooks/useQuery';
import { type QueryState } from 'src/core/api/queryState';

import { getSpanishDprSettingsQueryKey } from './query-key-registry';

import { type SetSpanishDprSettingsApi } from '.';

/**
 * Query and cache config
 */

export type SpanishDprSettingsResponse = {
  settings: {
    fiscalYearEndMonth: number;
    fiscalYearEndDay: number;
  };
};

export const useUpdateSpanishDprSettingsQueryCache = () => {
  const companyId = useCompanyId();

  const queryClient = useQueryClient();

  return async (payload: SetSpanishDprSettingsApi.Payload): Promise<void> => {
    const queryKey = getSpanishDprSettingsQueryKey(companyId);

    const previousData =
      queryClient.getQueryData<SpanishDprSettingsResponse>(queryKey);

    if (previousData) {
      queryClient.setQueryData<SpanishDprSettingsResponse>(queryKey, {
        ...previousData,
        settings: {
          fiscalYearEndMonth: payload.fiscalYearEndMonth,
          fiscalYearEndDay: payload.fiscalYearEndDay,
        },
      });
    }
  };
};

export const useInvalidateSpanishDprSettingsQueryCache = () => {
  const queryClient = useQueryClient();

  return async (companyId: string): Promise<void> => {
    await queryClient.invalidateQueries(
      getSpanishDprSettingsQueryKey(companyId),
    );
  };
};

/**
 * REST query hook
 */
export const useSpanishDprSettings =
  (): QueryState<SpanishDprSettingsResponse> => {
    const companyId = useCompanyId();

    return useQuery<SpanishDprSettingsResponse, SpanishDprSettingsResponse>({
      isEnabled: true,
      key: getSpanishDprSettingsQueryKey(companyId),
      request: {
        type: 'rest',
        target: 'companyAPI',
        endpoint: '/bookkeeping/spanish-dpr/settings',
      },
      options: {
        cacheTime: 10 * 60 * 1000 /* 10min */,
        staleTime: 10 * 60 * 1000 /* 10min */,
      },
    });
  };
